/**
 * AtmosphereReactivityDriver — the fourth atmosphere driver (sibling of
 * CombatMoodDriver / BossMoodDriver / ChoreographyDriver), built to the
 * world-combat reactivity spec (docs/plans/fx-grammar-specs/world-combat-reactivity.md).
 *
 * Where CombatMoodDriver folds combat into ONE anonymous global scalar, this
 * driver carries the missing dimensions — position, event kind, school — and
 * resolves them through the family-keyed response table into transient
 * envelopes + positional handle kicks:
 *
 *   emit sites (reactivityPulse helper / ABILITY_CASTED / kill signals)
 *     → EVENTS.ATMOSPHERE_PULSE (or direct subscriptions below)
 *     → REACTIVITY_RESPONSE[pulse × school × biome-family] lookup
 *     → AtmosphereLayer.pushPulse (ReactivityModel gates: budget / cooldown-
 *       merge / flash ceiling) → decay-to-1 factors in tickModulation.
 *
 * Signal wiring (census-verified):
 *  - `bigCast` rides EVENTS.ABILITY_CASTED — the proven CombatMoodDriver input
 *    (`ability:cast` has NO emitters). Only wound-up casts (windUpMs > 0)
 *    qualify; caster position via findStatusTargetPosition.
 *  - `kill` rides EVENTS.NPC_DEFEATED (live, wire-fed — reaches every client
 *    in multiplayer; the death-beats census proved EVENTS.MOB_DIED is
 *    single-player-only). Position from the DeathSnapshotStore
 *    (capture-before-removal, read-only) with a live-mob fallback.
 *    EVENTS.MOB_DIED is ALSO subscribed for the single-player path (it carries
 *    position directly); the kill cooldown-merge dedups any overlap.
 *  - `heavyImpact` / `reactionBeat` arrive via the reactivityPulse helper
 *    (CombatEventVFXHandler emit lines). `playerDamaged` is reserved for the
 *    phase-2 vignette hook.
 *
 * Reduce-motion holds the driver calm (pulses dropped, like
 * CombatMoodDriver.ts:41) AND the modulation ticker never attaches — double
 * safety, zero churn. Client-presentation only — no server/sim impact.
 */
import { EventBus, EVENTS } from '../../../shared/EventBus';
import { gameLogger } from '../../../utils/GameLogger';
import { getMotionPrefs } from '../accessibility/motionPrefs';
import { REACTIVITY, resolveResponses, type ReactivityPulseKind } from '../../../config/atmosphere/reactivity.config';
import { AtmosphereLayer } from '../../effects/atmosphere/AtmosphereLayer';
import { biomeFamilyOf } from '../../rendering/textures/BiomeTextureModule';
import { VisualLifecycleStore } from '../../diagnostics/VisualLifecycleStore';
import { getAbilityData } from '../../../shared/content/generated/abilities.gen';
import { findStatusTargetPosition, findMobPosition } from '../../effects/handlers/entityPositions';
import { DeathSnapshotStore } from '../combat/DeathSnapshotStore';
import type { AbilityCastedEvent, NpcDefeatedEvent } from '../../../shared/types/NetworkMessages';

class AtmosphereReactivityDriver {
  private unsubs: Array<() => void> = [];
  private started = false;

  /** Idempotent. No-op when disabled in config. */
  start(): void {
    if (this.started || !REACTIVITY.enabled) return;
    this.started = true;
    const bus = EventBus.getInstance();
    // The generic pulse lane (reactivityPulse helper — heavyImpact/reactionBeat
    // emit lines, choreography `emit` cues, future emit sites).
    this.unsubs.push(bus.on(EVENTS.ATMOSPHERE_PULSE, (p) => {
      this.handlePulse(p.kind, p.hasPosition ? { x: p.x, y: p.y } : null, p.intensity, p.school);
    }));
    // bigCast — the proven live cast signal (CombatMoodDriver precedent).
    this.unsubs.push(bus.on(EVENTS.ABILITY_CASTED, (msg) => this.onAbilityCasted(msg)));
    // kill — wire-fed, multiplayer-true (NPC_DEFEATED) + single-player (MOB_DIED).
    this.unsubs.push(bus.on(EVENTS.NPC_DEFEATED, (msg) => this.onNpcDefeated(msg)));
    this.unsubs.push(bus.on(EVENTS.MOB_DIED, (msg) => {
      this.handlePulse('kill', msg?.position ?? null, 1);
    }));
  }

  stop(): void {
    for (const u of this.unsubs) { try { u(); } catch { /* gone */ } }
    this.unsubs = [];
    this.started = false;
  }

  // ── signal adapters ───────────────────────────────────────────────

  private onAbilityCasted(msg: AbilityCastedEvent): void {
    const toml = msg?.ability_id ? getAbilityData(msg.ability_id) : undefined;
    const windUp = toml?.windUpMs ?? 0;
    if (windUp <= 0) return; // bigCast = a SIGNIFICANT (wound-up) cast only
    const pos = msg?.playerId ? findStatusTargetPosition(msg.playerId) : null;
    if (!pos) return;
    const intensity = Math.min(1, 0.4 + (windUp / REACTIVITY.bigCastWindUpNormMs) * 0.6);
    this.handlePulse('bigCast', pos, intensity, toml?.school);
  }

  private onNpcDefeated(msg: NpcDefeatedEvent): void {
    const id = msg?.npcId;
    if (!id) return;
    // Capture-before-removal snapshot first (the entity is deleted the same
    // tick it dies); live lookup as fallback. Read-only — this lane never
    // writes the store.
    const snap = DeathSnapshotStore.get(id);
    const pos = snap ? { x: snap.x, y: snap.y } : findMobPosition(id);
    if (!pos) return; // never tracked locally and no snapshot — stay quiet
    this.handlePulse('kill', pos, 1, snap?.lastSchool);
  }

  // ── the pulse funnel ──────────────────────────────────────────────

  private handlePulse(
    kind: ReactivityPulseKind,
    pos: { x: number; y: number } | null,
    intensity: number,
    school?: string,
  ): void {
    // Reduce-motion → hold the scene calm (the modulation ticker is off there
    // anyway; this avoids pooling envelopes that would evaluate to nothing).
    if (getMotionPrefs().reduceMotion) return;
    const biome = AtmosphereLayer.activeBiome();
    const family = biomeFamilyOf(biome);
    const responses = resolveResponses(kind, school, family);
    if (responses.length === 0) return; // this scene stays quiet for this pulse
    const verdict = AtmosphereLayer.pushPulse({
      kind,
      school: school ?? null,
      x: pos?.x ?? 0,
      y: pos?.y ?? 0,
      hasPosition: !!pos,
      intensity,
      responses,
    });
    const label = `${kind}${school ? `:${school}` : ''}${family ? `@${family}` : ''}`;
    if (verdict.accepted) {
      VisualLifecycleStore.record({
        type: 'atmos_pulse',
        label: `${label} i=${intensity.toFixed(2)}${verdict.merged ? ' (merged)' : ''}`,
        x: pos?.x ?? 0,
        y: pos?.y ?? 0,
        school: school ?? null,
        source: 'atmosphere-reactivity',
        meta: {
          responses: responses.map((r) => `${r.kind}/${r.channel}`),
          merged: verdict.merged,
          budgetScaled: verdict.budgetScale < 1,
          attackSkipped: verdict.attackSkipped,
        },
      });
    } else {
      // Rejected pulses record their reason at debug cadence (spec §6).
      VisualLifecycleStore.record({
        type: 'atmos_pulse',
        label: `${label} dropped (${verdict.dropped})`,
        x: pos?.x ?? 0,
        y: pos?.y ?? 0,
        school: school ?? null,
        source: 'atmosphere-reactivity',
        meta: { dropped: verdict.dropped },
      });
      gameLogger.debug('vfx', `[AtmosphereReactivity] ${label} dropped (${verdict.dropped})`);
    }
  }
}

/** Process singleton — one reactivity bridge for the client. */
export const atmosphereReactivityDriver = new AtmosphereReactivityDriver();

/** Start the world-combat reactivity bridge (called once at game bootstrap). Idempotent. */
export function startAtmosphereReactivityDriver(): void {
  atmosphereReactivityDriver.start();
}
